import { useEffect, useMemo, useState } from 'react';

import { Box, Modal, ModalBody, ModalContent, ModalOverlay, useDisclosure } from '@chakra-ui/react';
import { ReviewsMobileViewCell } from 'Deer/components/Reviews/ReviewsMobileViewCell';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useToast } from 'components/hooks/useToast';
import { isValid, parseISO } from 'date-fns';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import 'styles/agGrid.css';
import { agGridDateComparator } from 'utils/helpers/dateHelpers';

import NoDataImg from 'assets/assetsV1/illustrations/noReviews.svg';

import {
  MODULE_KEYS_USER_PREFERENCE_PERSISTENCE,
  REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP,
  REVIEW_SUBMISSION_STATUS_MAP,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';

import { withdrawNomination } from 'services/performanceReview';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { showErrorMessage, showLoader } from '../components/common';
import { calcInfoChipTextAndColor } from '../helpers';
import {
  PEER_STATUS_COLOR,
  PEER_STATUS_TEXT,
  getDeadlineEODTimeStamp,
} from './components/ReviewsPendingOnOthersComps/ReviewRowDisplay';
import { WithdrawConfirmation } from './components/ReviewsPendingOnOthersMenu/ReviewsPendingOnOthersMenu';
import { generatePendingOnOthersOptions } from './utils';

const TABLE_COLUMNS_DISPLAYNAME = {
  REVIEW_FROM: 'Review From',
  REVIEW_TYPE: 'Review Type',
  DUE_DATE: 'Due Date',
  REVIEW_CYCLE: 'Review Cycle',
  INSTANCE: 'Instance',
  STATUS: 'Status',
  EXPANDED_MENU: 'Expanded Menu',
};

const calculateStatusTextAndColor = ({ review }) => {
  let infoChipColor = 'yellow';
  let infoChipText = '';

  if (review.submissionStatus && (review.providerType === 'MANAGER' || review.providerType === 'UPWARD')) {
    infoChipText = REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].statusText;
    infoChipColor = REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].infoChipColor;
  } else {
    infoChipColor = PEER_STATUS_COLOR[review.peerStatus];
    infoChipText = PEER_STATUS_TEXT[review.peerStatus];
  }

  return [infoChipText, infoChipColor];
};

// need to do this because agGrid `valueFormatter` doesnt have any impact on exports
function getFormattedValue({ value, api, column, node }) {
  const displayName = api.getDisplayNameForColumn(column);
  const rowData = node.data;
  const cellId = column.getColId();
  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FROM: {
      const cellData = rowData[cellId];
      return cellData?.value?.displayName;
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_TYPE: {
      return REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP[value];
    }

    case TABLE_COLUMNS_DISPLAYNAME.DUE_DATE: {
      return value ?? 'NA';
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return value;
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return value;
    }

    case TABLE_COLUMNS_DISPLAYNAME.STATUS: {
      const { review } = Object.values(rowData)[0]?.reviewItem ?? {};
      const [infoChipText] = calculateStatusTextAndColor({ review });

      return infoChipText;
    }

    default:
      return new Error('Invalid column name');
  }
}

function getCellContent(displayName, cellId, rowData, value) {
  if (isMobileView()) {
    switch (displayName) {
      case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FROM: {
        const { deadline } = Object.values(rowData)[0]?.reviewItem ?? {};
        const deadlineTS = getDeadlineEODTimeStamp(deadline);
        const { infoChipColor, infoChipText } = calcInfoChipTextAndColor({
          reviewItem: Object.values(rowData)[0]?.reviewItem,
          reviewStatus: rowData[6]?.value,
        });
        const reviewDisplayData = rowData[cellId];
        const reviewTypeData = rowData[2]?.value;
        const reviewTypeValue = REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP[reviewTypeData];

        return (
          <ReviewsMobileViewCell
            profilePhoto={reviewDisplayData?.value?.profilePhoto}
            name={reviewDisplayData?.value?.displayName}
            subTitle={reviewTypeValue}
            description={{
              title: 'Due',
              dateProps: {
                date: deadlineTS,
              },
              text: 'NA',
            }}
            tagProps={{ color: infoChipColor, text: infoChipText }}
          />
        );
      }
      default:
        return null;
    }
  }

  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FROM: {
      const cellData = rowData[cellId];
      return (
        <ListItemComp
          title={cellData?.value?.displayName}
          LeftComponent={() => <Avatar src={cellData?.value?.profilePhoto} />}
          isBoldText={false}
          withoutBackground
        />
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_TYPE: {
      return (
        <Box className="info" w="76px">
          <TextBodyMedium className="info">{REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP[value]}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.DUE_DATE: {
      const dateTS = parseISO(value);
      return (
        <Box className="info">{isValid(dateTS) ? <DateComp date={dateTS} /> : <TextBodyMedium>NA</TextBodyMedium>}</Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.STATUS: {
      const { review } = Object.values(rowData)[0]?.reviewItem ?? {};
      const [infoChipText, infoChipColor] = calculateStatusTextAndColor({ review });

      return (
        <Box className="info">
          <TagColored color={infoChipColor}>{infoChipText}</TagColored>
        </Box>
      );
    }

    default:
      return new Error('Invalid column name');
  }
}

function CellRenderer(params) {
  return getCellContent(params.colDef.displayName, params.colDef.id, params.data, params.value);
}

function getColProps(col) {
  const defaultColProps = {
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    headerComponent: CustomHeaderCell,
  };

  switch (col.displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FROM: {
      return {
        ...defaultColProps,
        pinned: 'left',
        lockPosition: 'left',
        cellClass: 'locked-col',
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data[params.colDef.id].value?.displayName,
        cellRenderer: CellRenderer,
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_TYPE: {
      return {
        ...defaultColProps,
        sortable: true,
        filter: 'agSetColumnFilter',
        maxWidth: 220,

        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.DUE_DATE: {
      return {
        ...defaultColProps,
        sortable: true,
        maxWidth: 160,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agDateColumnFilter',
        filterValueGetter: ({ data }) => {
          if (!data[3]?.value) return null;
          return data[3]?.value;
        },
        filterParams: {
          comparator: agGridDateComparator,
        },
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agTextColumnFilter',
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agTextColumnFilter',
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.STATUS: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agSetColumnFilter',
        maxWidth: 220,
      };
    }

    default:
      return new Error('Invalid column name');
  }
}

const ReviewsPendingOnOthersAGGrid = ({
  selectedTableRef,
  columns = [],
  rows = [],
  removeFromReviewsList,
  loading,
  errorMessage,
}) => {
  const [rowData, setRowData] = useState([]);

  const [columnDefs, setColumnDefs] = useState([]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [reviewBeingWithdrawn, setReviewBeingWithdrawn] = useState(null);
  const [employeeBeingWithdrawn, setEmployeeBeingWithdrawn] = useState(null);

  const [forcedValue, forceUpdate] = useForceUpdate();

  const toast = useToast();

  useEffect(() => {
    if (columns.length === 0) return;

    if (isMobileView()) {
      // Only show the first column in mobile view
      const mobileColumnDefs = columns.slice(0, 1).map((col) => ({
        colId: col.id,
        field: col.id,
        id: col.id,
        displayName: col.displayName,
        minWidth: 255,
        flex: 1,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          suppressHeaderMenuButton: true,
          enableSorting: false,
        },
        headerValueGetter: () => col.displayName,
        ...getColProps(col),
        pinned: undefined,
        lockPosition: undefined,
        suppressMovable: true,
        suppressMenu: true,
        suppressSizeToFit: false,
      }));
      setColumnDefs(mobileColumnDefs);
    } else {
      const newColumnDefs = columns.map((col) => {
        const defaultColsProps = {
          colId: col.id,
          field: col.id,
          id: col.id,
          displayName: col.displayName,
          minWidth: 220,
          hide: col.displayName === 'Instance',
          flex: 1,
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
          },
          headerValueGetter: () => col.displayName,
          ...getColProps(col),
        };
        if (col.displayName === 'Review From' || col.displayName === 'Review Type') {
          return {
            ...defaultColsProps,
            lockVisible: true,
          };
        }
        return defaultColsProps;
      });
      setColumnDefs(newColumnDefs);
    }
  }, [columns]);

  useEffect(() => {
    setRowData([...rows]);
  }, [rows, forcedValue]);

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'MyReviews';
    const tabId = 'Explore';
    const uniqueId = 'PendingOnOthersGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const handleWithdraw = async () => {
    try {
      await withdrawNomination({
        reviewId: reviewBeingWithdrawn?.feedbackForm?.id,
        providerEmpId: employeeBeingWithdrawn?.id,
      });
      removeFromReviewsList(reviewBeingWithdrawn?.feedbackForm?.id, employeeBeingWithdrawn?.id);
      onClose();
    } catch (error) {
      console.error('Error in withdrawing nomination', error);
      toast({
        title: error.message || 'Some error occurred',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    }
  };

  const optionsGenerator = (data) => {
    const options = generatePendingOnOthersOptions({
      data,
      forceUpdate,
      toast,
      setEmployeeBeingWithdrawn,
      setReviewBeingWithdrawn,
      onOpen,
    });
    return options;
  };

  if (loading) {
    return showLoader();
  }
  if (rows === null) {
    return showErrorMessage({ errorText: errorMessage });
  }
  if (rows.length === 0) {
    return (
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading="There are no reviews pending on others"
          description="Well well well, it looks like you haven't received any reviews just yet! But don't worry, keep being awesome and they'll be sure to come pouring in."
          illustrationSrc={NoDataImg}
        />
      </div>
    );
  }

  return (
    <>
      <Box className="review">
        <AgGridTable
          key={gridStatePersistenceKey}
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={selectedTableRef}
          rowData={rowData}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination
          suppressPaginationPanel={rowData?.length < 6 || rowData?.length === undefined || rowData?.length === 0}
          paginationPageSize={isMobileView() ? 5 : 10}
          sideBar={!isMobileView() && sideBar}
          headerHeight={36}
          autoHeight={false}
          rowHeight={isMobileView() ? 100 : 56}
          suppressCellFocus
          defaultCsvExportParams={{
            processCellCallback: getFormattedValue,
            fileName: 'Pending_on_others.csv',
            columnKeys: columns.map((c) => c.id),
          }}
          kebabMenuIsRowSpecific
          kebabMenuOptionsGenerator={optionsGenerator}
          containerAutoHeight
          additionalMobileProps={isMobileView() ? { pinned: undefined, lockPosition: undefined } : undefined}
          isMobileView={isMobileView()}
          paginationPageSizeSelector={isMobileView() ? false : [10, 20, 50, 100]}
        />
      </Box>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent minW="550px" mt="12%">
          <ModalBody p={0}>
            <WithdrawConfirmation
              displayName={employeeBeingWithdrawn?.displayName}
              onCancel={onClose}
              onAccept={handleWithdraw}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ReviewsPendingOnOthersAGGrid;
